import {
  BackupRepository,
  CalendarsRepository,
  CapacitorSqliteAdapter,
  IndexedDbPersistence,
  LabelsRepository,
  MemoryAdapter,
  migrate,
  RecurrenceOverridesRepository,
  RecurrenceRepository,
  SettingsRepository,
  TasksRepository,
  TauriSqliteAdapter,
  ThemesRepository,
  TopicsRepository,
} from "@app/storage";
import type { StorageAdapter } from "@app/storage";

export type PlatformKind = "tauri" | "capacitor" | "browser";

/** Set when the native SQLite store could not be opened and the app fell back to
 *  an EMPTY IndexedDB store (see {@link initStorage}). The app then looks like a
 *  fresh install even though the real database is still on disk, so the UI must
 *  say so instead of letting the user assume their data is gone and start over
 *  in a store that the next successful boot will hide again. Read by
 *  `Shell.tsx`; null on every healthy boot. */
let storageFallbackError: string | null = null;

export function getStorageFallbackError(): string | null {
  return storageFallbackError;
}

export function detectPlatform(): PlatformKind {
  if (typeof window === "undefined") return "browser";
  const w = window as unknown as { __TAURI__?: unknown; Capacitor?: { isNativePlatform?: () => boolean } };
  if (w.__TAURI__) return "tauri";
  if (w.Capacitor?.isNativePlatform?.()) return "capacitor";
  return "browser";
}

export interface Services {
  db: StorageAdapter;
  tasks: TasksRepository;
  labels: LabelsRepository;
  topics: TopicsRepository;
  calendars: CalendarsRepository;
  recurrence: RecurrenceRepository;
  recurrenceOverrides: RecurrenceOverridesRepository;
  settings: SettingsRepository;
  themes: ThemesRepository;
  backup: BackupRepository;
}

let services: Services | null = null;

/** Pick the right adapter for the current platform, migrate, wire repos.
 *  `ephemeral` (ghost-demo iframes) = a fresh non-persisted MemoryAdapter:
 *  never reads or writes the user's real data, nothing to flush. */
export async function initStorage(opts?: { ephemeral?: boolean }): Promise<Services> {
  if (services) return services;
  if (opts?.ephemeral) {
    const db = new MemoryAdapter();
    await db.open();
    await migrate(db);
    services = buildServices(db);
    return services;
  }
  const platform = detectPlatform();
  let db: StorageAdapter = selectAdapter(platform);
  try {
    await db.open();
    await migrate(db);
  } catch (e) {
    // Android: a native SQLite failure must not brick the app — fall back to
    // the browser path (IndexedDB-persisted memory adapter; the WebView has
    // IndexedDB). The cause is kept on window for chrome://inspect / logcat.
    if (platform !== "capacitor") throw e;
    console.error("CapacitorSqliteAdapter failed; falling back to IndexedDB persistence", e);
    storageFallbackError = String(e);
    (window as unknown as Record<string, unknown>)["__storageFallbackError"] = storageFallbackError;
    db = new MemoryAdapter(new IndexedDbPersistence("reminders"));
    await db.open();
    await migrate(db);
  }
  // The memory adapter debounce-saves to IndexedDB (~150ms) — flush when the
  // page is hidden/unloading so a just-made edit isn't lost with the tab.
  if (db instanceof MemoryAdapter && typeof window !== "undefined") {
    const adapter = db;
    const flush = () => { void adapter.flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush();
    });
  }
  services = buildServices(db);
  return services;
}

function buildServices(db: StorageAdapter): Services {
  return {
    db,
    tasks: new TasksRepository(db),
    labels: new LabelsRepository(db),
    topics: new TopicsRepository(db),
    calendars: new CalendarsRepository(db),
    recurrence: new RecurrenceRepository(db),
    recurrenceOverrides: new RecurrenceOverridesRepository(db),
    settings: new SettingsRepository(db),
    themes: new ThemesRepository(db),
    backup: new BackupRepository(db),
  };
}

export function getServices(): Services {
  if (!services) throw new Error("initStorage() not called yet");
  return services;
}

function selectAdapter(platform: PlatformKind): StorageAdapter {
  // TauriSqliteAdapter only calls into @tauri-apps/plugin-sql inside open(),
  // so it's safe to import its class even in non-Tauri builds.
  if (platform === "tauri") return new TauriSqliteAdapter();
  // Android (Capacitor) gets real native SQLite, same as desktop.
  if (platform === "capacitor") return new CapacitorSqliteAdapter();
  // Browser persists the in-memory store to IndexedDB so reloads keep data.
  return new MemoryAdapter(new IndexedDbPersistence("reminders"));
}
